import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Shield, CheckCircle, ArrowRight, TrendingUp, Sparkles, Mail, ChevronDown, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

export default function Landing() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);
      
      const response = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });
      
      if (!response.ok) {
        throw new Error('Invalid credentials');
      }
      
      const data = await response.json();
      localStorage.setItem('auth_token', data.access_token);
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(t('landing.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const redirectUri = isLocalhost 
        ? 'http://localhost:8000/api/v1/auth/google/callback'
        : `${window.location.origin}/api/v1/auth/google/callback`;
      
      const response = await fetch(`/api/v1/auth/google/authorize?redirect_uri=${encodeURIComponent(redirectUri)}`);
      const data = await response.json();
      
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (error) {
      toast.error(t('landing.googleSignInFailed'));
    }
  };

  const faqs = [
    { question: t('landing.faqQ1'), answer: t('landing.faqA1') },
    { question: t('landing.faqQ2'), answer: t('landing.faqA2') },
    { question: t('landing.faqQ3'), answer: t('landing.faqA3') },
    { question: t('landing.faqQ4'), answer: t('landing.faqA4') },
    { question: t('landing.faqQ5'), answer: t('landing.faqA5') },
    { question: t('landing.faqQ6'), answer: t('landing.faqA6') },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <FileText className="w-8 h-8 text-indigo-600" />
              <span className="font-brand text-3xl font-bold text-gray-900 tracking-tight">DocIntel</span>
            </div>
            <Button
              onClick={() => setShowLogin(!showLogin)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6"
            >
              {t('landing.signIn')}
            </Button>
          </div>
        </div>
      </header>

      {/* Login Modal */}
      {showLogin && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={t('landing.signIn')} onClick={(e) => { if (e.target === e.currentTarget) setShowLogin(false); }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
            <button
              onClick={() => setShowLogin(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label={t('common.close')}
            >
              ✕
            </button>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('landing.signInToDocIntel')}</h2>

            <form onSubmit={handleEmailSignIn} className="space-y-4 mb-6">
              <Input
                type="email"
                placeholder={t('landing.loginEmailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full"
              />
              <Input
                type="password"
                placeholder={t('landing.loginPasswordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full"
              />
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3"
              >
                {loading ? t('landing.signingIn') : t('landing.signInButton')}
              </Button>
            </form>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">{t('landing.orContinueWith')}</span>
              </div>
            </div>

            <Button
              onClick={handleGoogleSignIn}
              className="w-full bg-white hover:bg-gray-50 text-gray-900 border border-gray-300 py-3"
            >
              <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
              </svg>
              {t('landing.signInWithGoogle')}
            </Button>

            <div className="mt-6 text-center text-sm text-gray-500">
              <p>{t('landing.restrictedAccess')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <div className="inline-block mb-4 px-4 py-2 bg-indigo-100 text-indigo-800 rounded-full text-sm font-semibold">
            {t('landing.heroBadge')}
          </div>
          <h1 className="text-6xl font-bold text-gray-900 mb-6 leading-tight">
            {t('landing.heroTitleLine1')}<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600">
              {t('landing.heroTitleHighlight')}
            </span>
          </h1>
          <p className="text-2xl text-gray-600 mb-4 max-w-4xl mx-auto">
            {t('landing.heroSubtitle')}
          </p>
          <p className="text-xl text-gray-500 mb-8 max-w-3xl mx-auto">
            {t('landing.heroSubtitle2')}
          </p>
          <div className="flex justify-center gap-4 mb-8">
            <a
              href="mailto:support@honestdev.co?subject=DocIntel%20Demo%20Request"
              className="inline-flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-lg text-lg font-medium transition-colors"
            >
              {t('landing.heroCta')} <ArrowRight className="ml-2 w-5 h-5" />
            </a>
            <Button
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              variant="outline"
              className="px-8 py-6 text-lg border-2"
            >
              {t('landing.heroCtaSecondary')} <Mail className="ml-2 w-5 h-5" />
            </Button>
          </div>
          <p className="text-sm text-gray-500 max-w-2xl mx-auto">
            {t('landing.heroFootnote')}
          </p>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-gradient-to-br from-slate-50 to-indigo-50">
        <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">{t('landing.problemTitle')}</h2>
        <p className="text-xl text-center text-gray-600 mb-12 max-w-3xl mx-auto">
          {t('landing.problemSubtitle')}
        </p>
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-2">{t('landing.problemCompliance')}</h3>
            <p className="text-gray-600">{t('landing.problemComplianceDesc')}</p>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-2">{t('landing.problemContracts')}</h3>
            <p className="text-gray-600">{t('landing.problemContractsDesc')}</p>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-2">{t('landing.problemRfp')}</h3>
            <p className="text-gray-600">{t('landing.problemRfpDesc')}</p>
          </div>
          <div className="bg-white p-6 rounded-xl border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-2">{t('landing.problemLegal')}</h3>
            <p className="text-gray-600">{t('landing.problemLegalDesc')}</p>
          </div>
        </div>
        <div className="text-center bg-white p-8 rounded-xl border-2 border-indigo-200 max-w-3xl mx-auto">
          <p className="text-xl text-gray-700 mb-2">
            <strong>{t('landing.problemStat')}</strong>
          </p>
          <p className="text-lg text-gray-600">
            {t('landing.problemStatSub')}
          </p>
          <p className="text-xl font-semibold text-indigo-600 mt-4">
            {t('landing.problemBetterWay')}
          </p>
        </div>
      </section>

      {/* Solution Overview */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-white">
        <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">{t('landing.solutionTitle')}</h2>
        <p className="text-xl text-center text-gray-600 mb-12 max-w-3xl mx-auto">
          {t('landing.solutionSubtitle')}
        </p>
        
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-xl border border-indigo-200">
            <div className="flex items-center gap-3 mb-3">
              <Shield className="w-6 h-6 text-indigo-600" />
              <h3 className="text-xl font-bold text-gray-900">{t('landing.solutionSearch')}</h3>
            </div>
            <p className="text-gray-600">{t('landing.solutionSearchDesc')}</p>
          </div>

          <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-6 rounded-xl border border-cyan-200">
            <div className="flex items-center gap-3 mb-3">
              <FileText className="w-6 h-6 text-cyan-600" />
              <h3 className="text-xl font-bold text-gray-900">{t('landing.solutionForms')}</h3>
            </div>
            <p className="text-gray-600">{t('landing.solutionFormsDesc')}</p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
            <div className="flex items-center gap-3 mb-3">
              <TrendingUp className="w-6 h-6 text-purple-600" />
              <h3 className="text-xl font-bold text-gray-900">{t('landing.solutionGap')}</h3>
            </div>
            <p className="text-gray-600">{t('landing.solutionGapDesc')}</p>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-red-50 p-6 rounded-xl border border-orange-200">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle className="w-6 h-6 text-orange-600" />
              <h3 className="text-xl font-bold text-gray-900">{t('landing.solutionInconsistency')}</h3>
            </div>
            <p className="text-gray-600">{t('landing.solutionInconsistencyDesc')}</p>
          </div>
        </div>

        <div className="text-center bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-8 rounded-xl">
          <p className="text-2xl font-bold mb-2">{t('landing.solutionStat')}</p>
          <p className="text-lg opacity-90">{t('landing.solutionStatSub')}</p>
        </div>
      </section>

      {/* Use Case Examples */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-gradient-to-br from-slate-50 to-indigo-50">
        <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">{t('landing.useCasesTitle')}</h2>
        <p className="text-xl text-center text-gray-600 mb-12 max-w-3xl mx-auto">
          {t('landing.useCasesSubtitle')}
        </p>
        
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white p-8 rounded-xl border-2 border-indigo-200">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-8 h-8 text-indigo-600" />
              <h3 className="text-2xl font-bold text-gray-900">{t('landing.doraTitle')}</h3>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold text-gray-500 mb-1">{t('landing.challengeLabel')}:</p>
                <p className="text-gray-700">{t('landing.doraChallenge')}</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <p className="text-sm font-semibold text-red-900 mb-1">{t('landing.manualProcessLabel')}:</p>
                <p className="text-red-800" dangerouslySetInnerHTML={{ __html: t('landing.doraManual') }} />
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-sm font-semibold text-green-900 mb-1">{t('landing.withDocIntelLabel')}:</p>
                <p className="text-green-800" dangerouslySetInnerHTML={{ __html: t('landing.doraDocIntel') }} />
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <p className="text-sm font-semibold text-indigo-900 mb-1">{t('landing.resultLabel')}:</p>
                <p className="text-indigo-800">{t('landing.doraResult')}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl border-2 border-cyan-200">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-8 h-8 text-cyan-600" />
              <h3 className="text-2xl font-bold text-gray-900">{t('landing.socTitle')}</h3>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold text-gray-500 mb-1">{t('landing.challengeLabel')}:</p>
                <p className="text-gray-700">{t('landing.socChallenge')}</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <p className="text-sm font-semibold text-red-900 mb-1">{t('landing.manualProcessLabel')}:</p>
                <p className="text-red-800" dangerouslySetInnerHTML={{ __html: t('landing.socManual') }} />
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-sm font-semibold text-green-900 mb-1">{t('landing.withDocIntelLabel')}:</p>
                <p className="text-green-800" dangerouslySetInnerHTML={{ __html: t('landing.socDocIntel') }} />
              </div>
              <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                <p className="text-sm font-semibold text-cyan-900 mb-1">{t('landing.resultLabel')}:</p>
                <p className="text-cyan-800">{t('landing.socResult')}</p>
              </div>
              <p className="text-xs text-gray-500 italic mt-2">*{t('landing.socDisclaimer')}</p>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl border-2 border-purple-200">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="w-8 h-8 text-purple-600" />
              <h3 className="text-2xl font-bold text-gray-900">{t('landing.rfpUseCaseTitle')}</h3>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold text-gray-500 mb-1">{t('landing.challengeLabel')}:</p>
                <p className="text-gray-700">{t('landing.rfpChallenge')}</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <p className="text-sm font-semibold text-red-900 mb-1">{t('landing.manualProcessLabel')}:</p>
                <p className="text-red-800" dangerouslySetInnerHTML={{ __html: t('landing.rfpManual') }} />
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-sm font-semibold text-green-900 mb-1">{t('landing.withDocIntelLabel')}:</p>
                <p className="text-green-800" dangerouslySetInnerHTML={{ __html: t('landing.rfpDocIntel') }} />
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <p className="text-sm font-semibold text-purple-900 mb-1">{t('landing.resultLabel')}:</p>
                <p className="text-purple-800">{t('landing.rfpResult')}</p>
              </div>
              <p className="text-xs text-gray-500 italic mt-2">*{t('landing.rfpDisclaimer')}</p>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl border-2 border-orange-200">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-8 h-8 text-orange-600" />
              <h3 className="text-2xl font-bold text-gray-900">{t('landing.legalResearchTitle')}</h3>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-semibold text-gray-500 mb-1">{t('landing.challengeLabel')}:</p>
                <p className="text-gray-700">{t('landing.legalChallenge')}</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <p className="text-sm font-semibold text-red-900 mb-1">{t('landing.manualProcessLabel')}:</p>
                <p className="text-red-800" dangerouslySetInnerHTML={{ __html: t('landing.legalManual') }} />
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-sm font-semibold text-green-900 mb-1">{t('landing.withDocIntelLabel')}:</p>
                <p className="text-green-800" dangerouslySetInnerHTML={{ __html: t('landing.legalDocIntel') }} />
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <p className="text-sm font-semibold text-orange-900 mb-1">{t('landing.resultLabel')}:</p>
                <p className="text-orange-800">{t('landing.legalResult')}</p>
              </div>
              <p className="text-xs text-gray-500 italic mt-2">*{t('landing.legalDisclaimer')}</p>
            </div>
          </div>

          <div className="bg-white p-8 rounded-xl border-2 border-emerald-200 md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <Briefcase className="w-8 h-8 text-emerald-600" />
              <h3 className="text-2xl font-bold text-gray-900">{t('landing.proposalTitle')}</h3>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-gray-500 mb-1">{t('landing.challengeLabel')}:</p>
                  <p className="text-gray-700">{t('landing.proposalChallenge')}</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <p className="text-sm font-semibold text-red-900 mb-1">{t('landing.manualProcessLabel')}:</p>
                  <p className="text-red-800" dangerouslySetInnerHTML={{ __html: t('landing.proposalManual') }} />
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-sm font-semibold text-green-900 mb-1">{t('landing.withDocIntelLabel')}:</p>
                  <p className="text-green-800" dangerouslySetInnerHTML={{ __html: t('landing.proposalDocIntel') }} />
                </div>
                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                  <p className="text-sm font-semibold text-emerald-900 mb-1">{t('landing.resultLabel')}:</p>
                  <p className="text-emerald-800">{t('landing.proposalResult')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">{t('landing.valueTitle')}</h2>
          <p className="text-xl text-center text-gray-600 mb-12">{t('landing.valueSubtitle')}</p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-8 rounded-xl border border-indigo-200">
              <div className="text-center mb-4">
                <TrendingUp className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('landing.valueSpeed')}</h3>
              </div>
              <p className="text-gray-700 text-center mb-4">
                {t('landing.valueSpeedDesc')}
              </p>
              <p className="text-sm text-gray-600 text-center italic">
                {t('landing.valueSpeedNote')}
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-8 rounded-xl border border-cyan-200">
              <div className="text-center mb-4">
                <CheckCircle className="w-12 h-12 text-cyan-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('landing.valueDetails')}</h3>
              </div>
              <p className="text-gray-700 text-center mb-4">
                {t('landing.valueDetailsDesc')}
              </p>
              <p className="text-sm text-gray-600 text-center italic">
                {t('landing.valueDetailsNote')}
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-xl border border-purple-200">
              <div className="text-center mb-4">
                <Sparkles className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('landing.valueScale')}</h3>
              </div>
              <p className="text-gray-700 text-center mb-4">
                {t('landing.valueScaleDesc')}
              </p>
              <p className="text-sm text-gray-600 text-center italic">
                {t('landing.valueScaleNote')}
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Social Proof */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-gradient-to-br from-slate-50 to-indigo-50">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">{t('landing.socialTitle')}</h2>
        <p className="text-xl text-center text-gray-600 mb-12">{t('landing.socialSubtitle')}</p>
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
            <div className="mb-4">
              <Shield className="w-8 h-8 text-indigo-600 mb-2" />
              <h3 className="text-lg font-bold text-gray-900">{t('landing.socialBanking')}</h3>
            </div>
            <p className="text-gray-700 mb-4">
              {t('landing.socialBankingDesc')}
            </p>
            <p className="text-sm text-gray-600 italic">{t('landing.socialBankingNote')}</p>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
            <div className="mb-4">
              <Shield className="w-8 h-8 text-cyan-600 mb-2" />
              <h3 className="text-lg font-bold text-gray-900">{t('landing.socialInsurance')}</h3>
            </div>
            <p className="text-gray-700 mb-4">
              {t('landing.socialInsuranceDesc')}
            </p>
            <p className="text-sm text-gray-600 italic">{t('landing.socialInsuranceNote')}</p>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
            <div className="mb-4">
              <FileText className="w-8 h-8 text-purple-600 mb-2" />
              <h3 className="text-lg font-bold text-gray-900">{t('landing.socialLawFirms')}</h3>
            </div>
            <p className="text-gray-700 mb-4">
              {t('landing.socialLawFirmsDesc')}
            </p>
            <p className="text-sm text-gray-600 italic">{t('landing.socialLawFirmsNote')}</p>
          </div>
        </div>
        <div className="bg-white p-8 rounded-xl border-2 border-indigo-200 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-6">{t('landing.overallMetrics')}</h3>
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div>
              <p className="text-4xl font-bold text-indigo-600 mb-2">{t('landing.metricReduction')}</p>
              <p className="text-gray-700">{t('landing.metricReductionDesc')}</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-cyan-600 mb-2">{t('landing.metricThroughput')}</p>
              <p className="text-gray-700">{t('landing.metricThroughputDesc')}</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-purple-600 mb-2">{t('landing.metricSystematic')}</p>
              <p className="text-gray-700">{t('landing.metricSystematicDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-white">
        <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">{t('landing.pricingTitle')}</h2>
        <p className="text-xl text-center text-gray-600 mb-12 max-w-3xl mx-auto">
          {t('landing.pricingSubtitle')}
        </p>
        <div className="grid md:grid-cols-2 gap-8 mb-12 max-w-5xl mx-auto">
          {/* Professional Tier */}
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-8 rounded-xl border-2 border-indigo-500 relative">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-indigo-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
              {t('landing.mostPopular')}
            </div>
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('landing.professional')}</h3>
              <div className="text-4xl font-bold text-indigo-600 mb-2">{t('landing.contactSales')}</div>
              <p className="text-gray-600">{t('landing.forGrowingTeams')}</p>
            </div>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: t('landing.allCoreFeatures') }} />
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('landing.unlimitedProjects')}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('landing.process1000Docs')}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('landing.allDocTypes')}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('landing.emailLiveChatSupport')}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('landing.apiAccess')}</span>
              </li>
            </ul>
            <a
              href="mailto:support@honestdev.co?subject=DocIntel%20Professional%20Plan"
              className="block w-full text-center bg-indigo-600 hover:bg-indigo-700 text-white py-2 rounded-md text-sm font-medium transition-colors"
            >
              {t('landing.contactSales')}
            </a>
          </div>

          {/* Enterprise Tier */}
          <div className="bg-white p-8 rounded-xl border-2 border-gray-200">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('landing.enterprise')}</h3>
              <div className="text-4xl font-bold text-gray-900 mb-2">{t('landing.custom')}</div>
              <p className="text-gray-600">{t('landing.forLargeOrgs')}</p>
            </div>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: t('landing.allModules') }} />
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('landing.unlimitedEverything')}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('landing.dedicatedManager')}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('landing.prioritySupport')}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{t('landing.customIntegrations')}</span>
              </li>
            </ul>
            <a
              href="mailto:support@honestdev.co?subject=DocIntel%20Enterprise%20Plan"
              className="block w-full text-center border-2 border-gray-300 hover:bg-gray-50 text-gray-900 py-2 rounded-md text-sm font-medium transition-colors"
            >
              {t('landing.talkToUs')}
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 bg-gradient-to-br from-slate-50 to-indigo-50">
        <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">{t('landing.faq')}</h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="border border-gray-200 rounded-lg">
              <button
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
              >
                <h3 className="text-lg font-bold text-gray-900 pr-8">{faq.question}</h3>
                <ChevronDown 
                  className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${
                    openFaq === index ? 'transform rotate-180' : ''
                  }`}
                />
              </button>
              {openFaq === index && (
                <div className="px-6 pb-6">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-white py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('landing.contactTitle')}</h2>
          <p className="text-xl text-gray-600 mb-8">{t('landing.contactSubtitle')}</p>

          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl shadow-xl p-10 max-w-xl mx-auto">
            <Mail className="w-14 h-14 text-indigo-600 mx-auto mb-6" />
            <p className="text-lg text-gray-700 mb-6">
              {t('landing.contactEmailCta')}
            </p>
            <a
              href="mailto:support@honestdev.co?subject=DocIntel%20Inquiry"
              className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
            >
              <Mail className="w-5 h-5" />
              support@honestdev.co
            </a>
            <p className="text-sm text-gray-500 mt-6">
              {t('landing.contactResponseTime')}
            </p>
          </div>
        </div>
      </section>
      
      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-12 text-center text-white">
          <Sparkles className="w-16 h-16 mx-auto mb-4" />
          <h2 className="text-4xl font-bold mb-4">{t('landing.ctaTitle')}</h2>
          <p className="text-2xl mb-4 opacity-90">{t('landing.ctaSubtitle1')}</p>
          <p className="text-xl mb-8 opacity-90">{t('landing.ctaSubtitle2')}</p>
          
          <a
            href="mailto:support@honestdev.co?subject=DocIntel%20Demo%20Request"
            className="inline-flex items-center bg-white text-indigo-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
          >
            <Mail className="w-5 h-5 mr-2" />
            {t('landing.ctaButton')}
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-600">
            <p>{t('landing.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
